
import Button from 'primevue/button';
import {defineComponent, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, reactive, ref,} from 'vue';
import { useI18n } from 'vue-i18n';
import PeriodSelector from '@/components/automated-scenarios/fields/PeriodSelector.vue';
import { ShopsConfiguration } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import {
  GetShopsConfigurationList,
  saveShopConfigurationsNest,
} from '@/composables/shop/ShopsConfiguration';
import { getConfigurationKeysLike } from '@/composables/configs/configuration';
import { Period } from '@/types/automated-scenarios';
import { TimeUnits } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import SpmButton from "@/components/spm-primevue/SpmButton.vue";
import { store } from '@/store';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { getFeatureFlagForService } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

export default defineComponent({
  name: 'MarketingPressure',
  components: {
    SpmButton,
    PeriodSelector,
    Button,
    SpmMessage,
    ConfirmDialog,
    ComingSoonBadge,
  },

  setup() {
    const { t } = useI18n();
    const isLoading = ref(false);
    const isResetting = ref(false);
    const confirm = useConfirm();
    const idShop = ref(UserState.activeShop?.id ?? 0);

    interface DelayData {
      email: Period;
      sms: Period;
      popUp: Period;
      push: Period;
      whatsapp: Period;
      richtexting: Period;
    }

    const channelKeys: Record<keyof DelayData, string> = {
      email: 'workflow_marketing_pressure_email_delay',
      sms: 'workflow_marketing_pressure_sms_delay',
      popUp: 'workflow_marketing_pressure_popup_delay',
      push: 'workflow_marketing_pressure_push_delay',
      whatsapp: 'workflow_marketing_pressure_whatsapp_delay',
      richtexting: 'workflow_marketing_pressure_richtexting_delay',
    };

    const delayData = reactive<DelayData>({
      email: { value: 0, unit: TimeUnits.MINUTE },
      sms: { value: 0, unit: TimeUnits.MINUTE },
      popUp: { value: 0, unit: TimeUnits.MINUTE },
      push: { value: 0, unit: TimeUnits.MINUTE },
      whatsapp: { value: 0, unit: TimeUnits.MINUTE },
      richtexting: { value: 0, unit: TimeUnits.MINUTE },
    })

    const delayConfig = ref([
      {
        key: 'email',
        label: t('templateBuilder.fields.email'),
        value: delayData.email,
        icon: 'fa-light fa-envelope',
      },
      {
        key: 'sms',
        label: t('automatedScenarios.actions.marketingPressure.input.sms'),
        value: delayData.sms,
        icon: 'fa-light fa-comment-sms',
      },
      {
        key: 'popUp',
        label: t('automatedScenarios.actions.marketingPressure.input.display'),
        value: delayData.popUp,
        icon: 'fa-light fa-window-restore',
      },
      {
        key: 'push',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.push'),
        value: delayData.push,
        icon: 'fa-light fa-bell',
      },
      {
        key: 'whatsapp',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.whatsapp'),
        value: delayData.whatsapp,
        icon: 'fa-brands fa-whatsapp',
        service: 'whatsapp_business',
      },
      {
        key: 'richtexting',
        label: t('automatedScenarios.actions.marketingPressure.scenarios.richtexting'),
        value: delayData.richtexting,
        icon: 'fa-light fa-comment-dots',
        service: 'rcs',
      },
    ])

    const delayUnits = [
      { value: TimeUnits.MINUTE, label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: TimeUnits.HOUR, label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: TimeUnits.DAY, label: t('automatedScenarios.fields.durations.day(s)') },
    ];


    const updateConfig = async() => {
      isLoading.value = true;
      // Enregistrement via NestJS (POST /shop/configurations) : il vide aussi le
      // cache Redis de la pression lu par le moteur, sinon l'ancien délai reste
      // appliqué jusqu'à 1 h.
      const saved = await saveShopConfigurationsNest(
        idShop.value,
        (Object.keys(channelKeys) as Array<keyof DelayData>).map((channel) => ({
          key: channelKeys[channel],
          value: JSON.stringify({ value: String(delayData[channel].value), unit: delayData[channel].unit }),
          lang: '',
        })),
      );
      if (saved) {
        showToastSuccess('savedSuccessful');
      } else {
        showToastError('noSave');
      }
      isLoading.value = false;
    }

    const parsePeriod = (raw: string): Period | null => {
      try {
        return JSON.parse(raw) as Period;
      } catch {
        return null;
      }
    };

    const fetchDefaults = async (): Promise<Record<keyof DelayData, Period>> => {
      const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
      const defaults = await getConfigurationKeysLike('workflow_marketing_pressure_%_delay_default');
      const defaultByKey = new Map<string, string>(defaults.map((item) => [item.key, item.value]));

      const result = {} as Record<keyof DelayData, Period>;
      channels.forEach((channel) => {
        const raw = defaultByKey.get(`${channelKeys[channel]}_default`);
        const period = raw ? parsePeriod(raw) : null;
        result[channel] = period ?? { value: 0, unit: TimeUnits.MINUTE };
      });
      return result;
    };

    const resetToDefaults = async () => {
      isResetting.value = true;
      try {
        const defaults = await fetchDefaults();
        const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
        const saved = await saveShopConfigurationsNest(
          idShop.value,
          channels.map((channel) => ({
            key: channelKeys[channel],
            value: JSON.stringify({ value: String(defaults[channel].value), unit: defaults[channel].unit }),
            lang: '',
          })),
        );
        if (!saved) {
          showToastError('noSave');
          return;
        }
        channels.forEach((channel) => {
          delayData[channel] = defaults[channel];
        });
        showToastSuccess('savedSuccessful');
      } catch {
        showToastError('noSave');
      } finally {
        isResetting.value = false;
      }
    };

    const confirmReset = () => {
      confirm.require({
        message: t('automatedScenarios.actions.marketingPressure.resetConfirm'),
        header: t('automatedScenarios.actions.marketingPressure.resetDefaults'),
        group: 'marketingPressureReset',
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-secondary',
        accept: resetToDefaults,
      });
    };

    onBeforeMount(async () => {
      store.commit('general/showTheSpinner');

      const channels = Object.keys(channelKeys) as Array<keyof DelayData>;
      const keyToChannel = new Map<string, keyof DelayData>(
        channels.map((channel) => [channelKeys[channel], channel]),
      );

      const shopConfigs = await GetShopsConfigurationList({
        shopId: idShop.value,
        key: 'workflow_marketing_pressure_%_delay',
        fields: 'id_shop_configuration, key, value',
        limit: 0,
        offset: 0,
      });

      const channelsWithShopValue = new Set<keyof DelayData>();
      if (shopConfigs) {
        shopConfigs.forEach((record: ShopsConfiguration) => {
          const channel = keyToChannel.get(record.key);
          if (!channel) return;
          const period = parsePeriod(record.value);
          if (!period) return;
          delayData[channel] = period;
          channelsWithShopValue.add(channel);
        });
      }

      const missingChannels = channels.filter((c) => !channelsWithShopValue.has(c));
      if (missingChannels.length > 0) {
        const defaults = await getConfigurationKeysLike('workflow_marketing_pressure_%_delay_default');
        const defaultByKey = new Map<string, string>(defaults.map((item) => [item.key, item.value]));
        missingChannels.forEach((channel) => {
          const raw = defaultByKey.get(`${channelKeys[channel]}_default`);
          if (!raw) return;
          const period = parsePeriod(raw);
          if (period) delayData[channel] = period;
        });
      }

      store.commit('general/hideTheSpinner');
    });

    return {
      t,
      isLoading,
      isResetting,
      delayConfig,
      delayData,
      delayUnits,
      updateConfig,
      confirmReset,
      getServiceFlag: getFeatureFlagForService,
    };
  },
});
